// Demo Booking Page - For testing and demonstrations
import React, { useState } from 'react';
import styled from 'styled-components';
import StripePayment from './StripePayment';
import CalendarIntegration from '../../../components/CalendarIntegration';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const DemoHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  margin-bottom: 30px;
  
  h1 {
    margin: 0 0 10px 0;
    font-size: 28px;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
    font-size: 16px;
  }
`;

const DemoCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  padding: 30px;
  margin-bottom: 20px;
`;

const PropertyHeader = styled.div`
  text-align: center;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
  margin-bottom: 30px;
`;

const PropertyTitle = styled.h2`
  color: #333;
  margin: 0 0 10px 0;
  font-size: 24px;
`;

const PropertySubtitle = styled.p`
  color: #666;
  margin: 0;
  font-size: 16px;
`;

const DemoForm = styled.form`
  display: grid;
  gap: 20px;
`;

const Section = styled.div`
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 18px;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.columns || '1fr'};
  gap: 15px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
  display: block;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
    box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
  }
`;

const TimeSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 15px;
`;

const TimeSlot = styled.button`
  padding: 12px;
  border: 2px solid ${props => props.selected ? '#007AFF' : '#ddd'};
  background: ${props => props.selected ? '#007AFF' : 'white'};
  color: ${props => props.selected ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.selected ? '#0056b3' : '#f0f0f0'};
  }
`;

const PricingSection = styled.div`
  background: #e3f2fd;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #90caf9;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  font-size: 18px;
  padding-top: 8px;
  border-top: 1px solid #90caf9;
`;

const SubmitButton = styled.button`
  padding: 15px 30px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #1e7e34;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const DemoNotice = styled.div`
  background: #fff3cd;
  color: #856404;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #ffeaa7;
  display: flex;
  align-items: center;
  gap: 10px;
  
  span {
    font-size: 24px;
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  white-space: pre-line;
  font-size: 14px;
  line-height: 1.5;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
`;

const DemoBookingPage = () => {
  const [formData, setFormData] = useState({
    showingDate: '',
    showingTime: '',
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    demoRecipientEmail: '',
    demoRecipientPhone: '',
    demoMailAddress: '',
    demoMailCity: '',
    demoMailState: 'CA',
    demoMailZip: '',
    agreeToTerms: false
  });

  const [showPayment, setShowPayment] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [success, setSuccess] = useState(null);
  const [bookingData, setBookingData] = useState(null);

  // Demo property data - this will be fetched from the real database
  const demoProperty = {
    id: 27, // Real property ID from database
    slug: 'demo-property-12345',
    address_line1: '123 Demo Street',
    unit_number: 'Suite 100',
    city: 'Los Angeles',
    state: 'CA',
    zip_code: '90210',
    showing_instructions: 'This is a demonstration property. All notifications are real!',
    agent_name: 'Demo Agent',
    agent_phone: '(555) 123-DEMO',
    agent_email: 'demo@showingbooker.com',
    is_occupied: false,
    showing_duration_minutes: 30,
    advance_notice_hours: 24
  };

  // Generate available dates (next 14 days, starting tomorrow)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { 
          weekday: 'long', 
          month: 'short', 
          day: 'numeric' 
        })
      });
    }
    return dates;
  };

  // Generate time slots
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        // Use current date for proper formatting
        const today = new Date();
        const label = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        slots.push({ value: time, label });
      }
    }
    return slots;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleTimeSlotSelect = (time) => {
    setFormData({
      ...formData,
      showingTime: time
    });
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.showingDate) errors.push('Please select a showing date');
    if (!formData.showingTime) errors.push('Please select a showing time');
    if (!formData.buyerName.trim()) errors.push('Please enter buyer name');
    if (!formData.buyerEmail.trim()) errors.push('Please enter buyer email');
    if (!formData.buyerPhone.trim()) errors.push('Please enter buyer phone');
    if (!formData.agreeToTerms) errors.push('Please agree to terms');
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      alert(validationErrors.join('\n'));
      return;
    }
    
    setShowPayment(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  };

  const handlePaymentSuccess = async (paymentResult) => {
    setPaymentComplete(true);
    setShowPayment(false);
    
    const booking = paymentResult.booking;
    const message = `🎉 DEMO BOOKING SUCCESSFUL!\n\n📋 Confirmation #${booking.confirmation_number}\n💳 Demo Payment: $1.00 (Processed)\n\n📬 REAL NOTIFICATIONS SENT:\n✓ SMS sent to: ${formData.demoRecipientPhone || 'Not provided'}\n✓ Email sent to: ${formData.demoRecipientEmail || formData.buyerEmail}\n✓ Certified mail scheduled to: ${formData.demoMailAddress || 'Not provided'}\n\n📅 DEMO SHOWING DETAILS:\nDate: ${new Date(booking.showingDate).toLocaleDateString()}\nTime: ${booking.showingTime}\nLocation: ${demoProperty.address_line1}, ${demoProperty.unit_number}\n\n🎪 This was a demonstration of ShowingBooker's full notification system!\nAll notifications were real and delivered to your specified contacts.`;
    
    setSuccess(message);
    setBookingData({
      ...booking,
      amount: 1.00
    });
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentError = (error) => {
    alert(`Payment error: ${error}`);
    setShowPayment(false);
  };

  const availableDates = getAvailableDates();
  const timeSlots = getTimeSlots();

  return (
    <Container>
      <DemoHeader>
        <h1>🎪 ShowingBooker Live Demo</h1>
        <p>Experience the complete property showing booking system with real notifications</p>
      </DemoHeader>

      <DemoCard>
        <DemoNotice>
          <span>🧪</span>
          <div>
            <strong>Live Demo Environment</strong>
            <div>This demo uses real payment processing ($1), SMS, email, and certified mail services. Perfect for testing and presentations!</div>
          </div>
        </DemoNotice>

        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        {paymentComplete && bookingData && (
          <CalendarIntegration 
            booking={bookingData}
            property={demoProperty}
            compact={false}
          />
        )}

        <PropertyHeader>
          <PropertyTitle>
            🏠 {demoProperty.address_line1}, {demoProperty.unit_number}
          </PropertyTitle>
          <PropertySubtitle>
            {demoProperty.city}, {demoProperty.state} {demoProperty.zip_code}
          </PropertySubtitle>
        </PropertyHeader>

        <DemoForm onSubmit={handleSubmit}>
          <Section>
            <SectionTitle>📅 Select Date & Time</SectionTitle>
            
            <FormRow>
              <div>
                <Label>Showing Date *</Label>
                <Select
                  name="showingDate"
                  value={formData.showingDate}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a date</option>
                  {availableDates.map(date => (
                    <option key={date.value} value={date.value}>
                      {date.label}
                    </option>
                  ))}
                </Select>
              </div>
            </FormRow>

            {formData.showingDate && (
              <div>
                <Label>Available Time Slots *</Label>
                <TimeSlotGrid>
                  {timeSlots.map(slot => (
                    <TimeSlot
                      key={slot.value}
                      type="button"
                      selected={formData.showingTime === slot.value}
                      onClick={() => handleTimeSlotSelect(slot.value)}
                    >
                      {slot.label}
                    </TimeSlot>
                  ))}
                </TimeSlotGrid>
              </div>
            )}
          </Section>

          <Section>
            <SectionTitle>👤 Buyer Information</SectionTitle>
            
            <FormRow columns="1fr 1fr">
              <div>
                <Label>Buyer Name *</Label>
                <Input
                  type="text"
                  name="buyerName"
                  value={formData.buyerName}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label>Buyer Email *</Label>
                <Input
                  type="email"
                  name="buyerEmail"
                  value={formData.buyerEmail}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </div>
            </FormRow>

            <FormRow>
              <div>
                <Label>Buyer Phone *</Label>
                <Input
                  type="tel"
                  name="buyerPhone"
                  value={formData.buyerPhone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>🧪 Demo Notification Settings</SectionTitle>
            <p style={{margin: '0 0 15px 0', color: '#666', fontSize: '14px'}}>
              Configure where you want to receive the demo notifications. Leave blank to use buyer info above.
            </p>
            
            <FormRow columns="1fr 1fr">
              <div>
                <Label>Demo SMS Phone</Label>
                <Input
                  type="tel"
                  name="demoRecipientPhone"
                  value={formData.demoRecipientPhone}
                  onChange={handleChange}
                  placeholder="Your phone for SMS demo"
                />
              </div>
              <div>
                <Label>Demo Email</Label>
                <Input
                  type="email"
                  name="demoRecipientEmail"
                  value={formData.demoRecipientEmail}
                  onChange={handleChange}
                  placeholder="Your email for demo"
                />
              </div>
            </FormRow>

            <FormRow columns="2fr 1fr 1fr">
              <div>
                <Label>Demo Mail Address</Label>
                <Input
                  type="text"
                  name="demoMailAddress"
                  value={formData.demoMailAddress}
                  onChange={handleChange}
                  placeholder="123 Your Street"
                />
              </div>
              <div>
                <Label>City</Label>
                <Input
                  type="text"
                  name="demoMailCity"
                  value={formData.demoMailCity}
                  onChange={handleChange}
                  placeholder="Los Angeles"
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  type="text"
                  name="demoMailZip"
                  value={formData.demoMailZip}
                  onChange={handleChange}
                  placeholder="90210"
                />
              </div>
            </FormRow>
          </Section>

          <PricingSection>
            <SectionTitle>💳 Demo Pricing</SectionTitle>
            <TotalRow>
              <span>Demo Booking Fee</span>
              <span>$1.00</span>
            </TotalRow>
            <p style={{margin: '10px 0 0 0', fontSize: '14px', color: '#666'}}>
              Demo uses $1 charge to test real payment processing. All other features are identical to production.
            </p>
          </PricingSection>

          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              required
            />
            <Label style={{ margin: 0 }}>
              I understand this is a demo with real notifications and a $1 charge *
            </Label>
          </CheckboxContainer>

          <SubmitButton 
            type="submit" 
            disabled={!formData.showingDate || !formData.showingTime || !formData.buyerName || !formData.buyerEmail || !formData.buyerPhone || !formData.agreeToTerms}
          >
            💳 Book Demo Showing - $1.00
          </SubmitButton>
        </DemoForm>

        {showPayment && !paymentComplete && (
          <StripePayment
            amount={1.00}
            bookingData={{
              propertyId: demoProperty.id,
              showingDate: formData.showingDate,
              showingTime: formData.showingTime,
              buyerName: formData.buyerName,
              buyerEmail: formData.demoRecipientEmail || formData.buyerEmail,
              buyerPhone: formData.demoRecipientPhone || formData.buyerPhone,
              buyerAgentName: formData.buyerAgentName,
              buyerAgentPhone: formData.buyerAgentPhone,
              specialRequests: formData.specialRequests || 'Demo booking for testing and presentations',
              // Add demo mail address if provided
              demoMailAddress: formData.demoMailAddress,
              demoMailCity: formData.demoMailCity,
              demoMailState: formData.demoMailState,
              demoMailZip: formData.demoMailZip,
              demoRecipientPhone: formData.demoRecipientPhone,
              demoRecipientEmail: formData.demoRecipientEmail
            }}
            propertyData={demoProperty}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        )}
      </DemoCard>
    </Container>
  );
};

export default DemoBookingPage;